import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { storesAPI } from '../lib/api';
import { optimizeCloudinaryUrl } from '../lib/cloudinary';
import { LocationMap } from '../components/LocationMap';
import { BottomNav } from './MarketplaceHome';
import { ReviewsSection } from '../components/ReviewsSection';
import {
  ArrowLeft,
  MapPin,
  Phone,
  MessageCircle,
  Mail,
  Instagram,
  Facebook,
  Globe,
  Clock,
  Star,
  ShieldCheck,
  ShoppingBag,
  Package,
  Award,
  ChevronRight,
  Store as StoreIcon,
  Play,
  Users,
} from 'lucide-react';

const formatPrice = (n) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(n || 0);

const onlyDigits = (s) => (s || '').replace(/\D/g, '');

const monthsSince = (isoDate) => {
  if (!isoDate) return null;
  try {
    const start = new Date(isoDate);
    const now = new Date();
    const diff = Math.max(0, (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth()));
    return diff;
  } catch (_) { return null; }
};

const ProductCardMini = ({ product }) => {
  const hasPromo = product.promo_price && product.promo_price < product.price;
  return (
    <Link
      to={`/produto/${product.id}`}
      className="flex-shrink-0 w-40 sm:w-44 bg-white rounded-xl border border-slate-100 shadow-sm hover:shadow-md transition-shadow overflow-hidden"
      data-testid={`inst-product-${product.id}`}
    >
      <div className="relative aspect-square bg-slate-50">
        <img
          src={optimizeCloudinaryUrl(product.images?.[0], { width: 400 }) || 'https://via.placeholder.com/200x200?text=Produto'}
          alt={product.name}
          loading="lazy"
          className="w-full h-full object-cover"
        />
        {hasPromo && (
          <span className="absolute top-2 left-2 bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow">
            -{Math.round((1 - product.promo_price / product.price) * 100)}%
          </span>
        )}
      </div>
      <div className="p-3">
        <h4 className="text-sm font-semibold text-slate-900 line-clamp-2 min-h-[2.5rem] leading-tight">
          {product.name}
        </h4>
        <div className="mt-1">
          {hasPromo ? (
            <div className="flex flex-col leading-tight">
              <span className="text-[11px] text-slate-400 line-through">{formatPrice(product.price)}</span>
              <span className="text-base font-bold text-green-600">{formatPrice(product.promo_price)}</span>
            </div>
          ) : (
            <span className="text-base font-bold text-slate-900">{formatPrice(product.price)}</span>
          )}
        </div>
      </div>
    </Link>
  );
};

const SectionCarousel = ({ id, title, icon: Icon, products, accent = 'blue' }) => {
  if (!products || products.length === 0) return null;
  const accentBg =
    accent === 'red' ? 'bg-red-50 text-red-700'
    : accent === 'amber' ? 'bg-amber-50 text-amber-700'
    : accent === 'emerald' ? 'bg-emerald-50 text-emerald-700'
    : accent === 'violet' ? 'bg-violet-50 text-violet-700'
    : 'bg-blue-50 text-blue-700';
  return (
    <section className="space-y-3" data-testid={`inst-section-${id}`}>
      <div className="flex items-center gap-2">
        <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${accentBg}`}>
          {Icon && <Icon className="w-3.5 h-3.5" />} {title}
        </span>
        <span className="text-xs text-slate-500">{products.length} {products.length === 1 ? 'produto' : 'produtos'}</span>
      </div>
      <div className="flex gap-3 overflow-x-auto snap-x snap-mandatory hide-scrollbar">
        {products.map((p) => (<ProductCardMini key={p.id} product={p} />))}
      </div>
    </section>
  );
};

export const StoreInstitutional = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    storesAPI.getInstitutional(id)
      .then(({ data }) => { if (alive) setData(data); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full" />
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 p-8">
        <StoreIcon className="w-10 h-10 text-slate-300" />
        <p className="text-slate-500">Loja não encontrada.</p>
        <button onClick={() => navigate('/lojas')} className="text-blue-600 font-medium">Ver todas as lojas</button>
      </div>
    );
  }

  const { store, credibility, sections } = data;
  const bannerUrl = store.banner_url ? optimizeCloudinaryUrl(store.banner_url, { width: 1600 }) : null;
  const logoUrl = store.logo_url ? optimizeCloudinaryUrl(store.logo_url, { width: 300 }) : null;
  const ownerPhotoUrl = store.owner_photo_url ? optimizeCloudinaryUrl(store.owner_photo_url, { width: 400 }) : null;
  const facadeUrl = store.facade_url ? optimizeCloudinaryUrl(store.facade_url, { width: 800 }) : null;
  const marketingPhotos = (store.marketing_photos || []).map((u) => optimizeCloudinaryUrl(u, { width: 800 }));
  const marketingVideos = store.marketing_videos || [];
  const wa = onlyDigits(store.whatsapp);
  const tel = onlyDigits(store.phone);
  const instaUser = (store.instagram || '').replace(/^@/, '').replace(/^https?:\/\/(www\.)?instagram\.com\//i, '').replace(/\/$/, '');
  const monthsOn = monthsSince(credibility?.on_platform_since);
  const timeLabel =
    monthsOn === null ? '—'
    : monthsOn < 1 ? 'Menos de 1 mês'
    : monthsOn < 12 ? `${monthsOn} ${monthsOn === 1 ? 'mês' : 'meses'} na plataforma`
    : `${Math.floor(monthsOn / 12)} ${Math.floor(monthsOn / 12) === 1 ? 'ano' : 'anos'} na plataforma`;

  return (
    <div className="min-h-screen bg-slate-50 pb-24" data-testid="store-institutional">
      {/* Banner */}
      <div
        className="relative h-56 sm:h-72 w-full overflow-hidden"
        style={
          bannerUrl
            ? { backgroundImage: `linear-gradient(0deg, rgba(4,26,77,0.55), rgba(4,26,77,0.15)), url(${bannerUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' }
            : { background: 'linear-gradient(135deg, #0A3FA0, #0047AB)' }
        }
        data-testid="inst-banner"
      >
        <button
          onClick={() => (window.history.state && window.history.state.idx > 0 ? navigate(-1) : navigate(`/loja/${id}`))}
          className="absolute top-4 left-4 w-10 h-10 rounded-full bg-white/90 backdrop-blur flex items-center justify-center shadow-lg"
          data-testid="inst-back-btn"
        >
          <ArrowLeft className="w-5 h-5 text-slate-800" />
        </button>
        {credibility?.verified && (
          <span className="absolute top-4 right-4 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-600 text-white text-xs font-bold shadow-lg" data-testid="inst-verified-badge">
            <ShieldCheck className="w-4 h-4" /> Loja Verificada
          </span>
        )}
      </div>

      {/* Store header card */}
      <div className="px-4 -mt-16 relative z-10">
        <div className="bg-white rounded-2xl shadow-lg border border-slate-100 p-4 sm:p-5">
          <div className="flex items-start gap-4">
            <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl bg-slate-100 overflow-hidden flex items-center justify-center flex-shrink-0 ring-2 ring-white shadow-md -mt-8">
              {logoUrl ? (
                <img src={logoUrl} alt={store.name} className="w-full h-full object-cover" data-testid="inst-logo" />
              ) : (
                <StoreIcon className="w-10 h-10 text-blue-600" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-xl sm:text-2xl font-extrabold text-slate-900 truncate" data-testid="inst-store-name">
                {store.fantasy_name || store.name}
              </h1>
              {store.city && (
                <p className="text-sm text-slate-500 flex items-center gap-1 mt-0.5">
                  <MapPin className="w-3.5 h-3.5" /> {store.city}/{store.state}
                </p>
              )}
              <div className="flex flex-wrap items-center gap-2 mt-2">
                {credibility?.avg_rating && (
                  <span className="inline-flex items-center gap-1 text-sm">
                    <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                    <span className="font-semibold text-slate-900">{credibility.avg_rating}</span>
                    <span className="text-xs text-slate-500">({credibility.total_reviews})</span>
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Credibility strip */}
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="inst-credibility">
            <div className="bg-slate-50 rounded-xl p-3 text-center">
              <ShoppingBag className="w-4 h-4 text-blue-600 mx-auto" />
              <p className="text-lg font-bold text-slate-900 mt-1">{credibility?.orders_delivered ?? 0}</p>
              <p className="text-[10px] uppercase tracking-wider text-slate-500">Pedidos entregues</p>
            </div>
            <div className="bg-slate-50 rounded-xl p-3 text-center">
              <Award className="w-4 h-4 text-emerald-600 mx-auto" />
              <p className="text-lg font-bold text-slate-900 mt-1">{credibility?.orders_total ?? 0}</p>
              <p className="text-[10px] uppercase tracking-wider text-slate-500">Pedidos totais</p>
            </div>
            <div className="bg-slate-50 rounded-xl p-3 text-center">
              <Star className="w-4 h-4 text-amber-500 mx-auto" />
              <p className="text-lg font-bold text-slate-900 mt-1">{credibility?.avg_rating ?? '—'}</p>
              <p className="text-[10px] uppercase tracking-wider text-slate-500">Avaliação</p>
            </div>
            <div className="bg-slate-50 rounded-xl p-3 text-center">
              <Clock className="w-4 h-4 text-violet-600 mx-auto" />
              <p className="text-sm font-bold text-slate-900 mt-1 leading-tight">{timeLabel}</p>
              <p className="text-[10px] uppercase tracking-wider text-slate-500">Tempo</p>
            </div>
          </div>

          {/* CTA */}
          <div className="mt-4 flex gap-2">
            <Link
              to={`/loja/${store.id}`}
              className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow-sm text-sm"
              data-testid="inst-see-all-products"
            >
              <Package className="w-4 h-4" /> Ver todos os produtos
            </Link>
            {store.whatsapp && (
              <a
                href={`https://wa.me/55${wa}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 px-4 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-sm"
                data-testid="inst-whatsapp-btn"
              >
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Owner / About / Mission */}
      <div className="px-4 mt-4 space-y-4">
        {(ownerPhotoUrl || store.history || store.mission) && (
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 sm:p-5" data-testid="inst-about">
            <div className="flex flex-col sm:flex-row gap-4">
              {ownerPhotoUrl && (
                <img src={ownerPhotoUrl} alt="Proprietário" loading="lazy" className="w-24 h-24 rounded-2xl object-cover ring-1 ring-slate-100 mx-auto sm:mx-0" data-testid="inst-owner-photo" />
              )}
              <div className="flex-1 space-y-3">
                {store.history && (
                  <div>
                    <h3 className="text-xs font-bold uppercase tracking-widest text-blue-700 mb-1">Nossa história</h3>
                    <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{store.history}</p>
                  </div>
                )}
                {store.mission && (
                  <div className="mt-4">
                    <h3 className="text-xs font-bold uppercase tracking-widest text-blue-700 mb-1">Missão</h3>
                    <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{store.mission}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Contact + hours + map */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 sm:p-5 space-y-3" data-testid="inst-contact">
          <h3 className="text-xs font-bold uppercase tracking-widest text-blue-700">Contato e localização</h3>
          <ul className="space-y-2 text-sm">
            {store.address && (
              <li className="flex gap-2"><MapPin className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" /><span className="text-slate-700">{store.address}</span></li>
            )}
            {store.operating_hours && (
              <li className="flex gap-2"><Clock className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" /><span className="text-slate-700">{store.operating_hours}{store.operating_days?.length ? ` · ${store.operating_days.join(', ')}` : ''}</span></li>
            )}
            {store.phone && (
              <li className="flex gap-2"><Phone className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" /><a href={`tel:+55${tel}`} className="text-slate-700 hover:text-blue-700">{store.phone}</a></li>
            )}
            {store.email && (
              <li className="flex gap-2"><Mail className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" /><a href={`mailto:${store.email}`} className="text-slate-700 hover:text-blue-700 break-all">{store.email}</a></li>
            )}
          </ul>

          {(store.instagram || store.facebook || store.website) && (
            <div className="flex flex-wrap gap-2 pt-2">
              {instaUser && (
                <a href={`https://instagram.com/${instaUser}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-pink-50 text-pink-700 text-xs font-medium hover:bg-pink-100" data-testid="inst-instagram">
                  <Instagram className="w-3.5 h-3.5" /> @{instaUser}
                </a>
              )}
              {store.facebook && (
                <a href={store.facebook.startsWith('http') ? store.facebook : `https://facebook.com/${store.facebook}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-50 text-blue-700 text-xs font-medium hover:bg-blue-100" data-testid="inst-facebook">
                  <Facebook className="w-3.5 h-3.5" /> Facebook
                </a>
              )}
              {store.website && (
                <a href={store.website.startsWith('http') ? store.website : `https://${store.website}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-100 text-slate-700 text-xs font-medium hover:bg-slate-200" data-testid="inst-website">
                  <Globe className="w-3.5 h-3.5" /> Site
                </a>
              )}
            </div>
          )}

          {(typeof store.lat === 'number' && typeof store.lng === 'number') && (
            <div className="pt-2" data-testid="inst-map">
              <LocationMap lat={store.lat} lng={store.lng} zoom={15} height="220px" markerText={store.name} />
            </div>
          )}
        </div>

        {/* Marketing gallery */}
        {(facadeUrl || marketingPhotos.length > 0 || marketingVideos.length > 0) && (
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 sm:p-5" data-testid="inst-marketing">
            <h3 className="text-xs font-bold uppercase tracking-widest text-blue-700 mb-3 inline-flex items-center gap-2">
              <Users className="w-3.5 h-3.5" /> Galeria da empresa
            </h3>
            <div className="flex gap-3 overflow-x-auto hide-scrollbar">
              {facadeUrl && (
                <img src={facadeUrl} alt="Fachada" loading="lazy" className="flex-shrink-0 w-52 h-40 object-cover rounded-xl" data-testid="inst-facade" />
              )}
              {marketingPhotos.map((url, i) => (
                <img key={i} src={url} alt={`Marketing ${i + 1}`} loading="lazy" className="flex-shrink-0 w-52 h-40 object-cover rounded-xl" data-testid={`inst-marketing-photo-${i}`} />
              ))}
              {marketingVideos.map((url, i) => (
                <div key={`v${i}`} className="flex-shrink-0 relative w-52 h-40 rounded-xl overflow-hidden bg-black" data-testid={`inst-marketing-video-${i}`}>
                  <video src={url} controls playsInline className="w-full h-full object-cover">
                    <track kind="captions" />
                  </video>
                  <span className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-black/60 text-white text-[10px] font-semibold">
                    <Play className="w-3 h-3" /> Vídeo
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Products by section */}
        <SectionCarousel id="promocoes" title="Promoções" icon={Award} products={sections?.promocoes} accent="red" />
        <SectionCarousel id="novidades" title="Novidades" icon={Star} products={sections?.novidades} accent="emerald" />
        <SectionCarousel id="mais-vendidos" title="Mais Vendidos" icon={ShoppingBag} products={sections?.mais_vendidos} accent="amber" />
        <SectionCarousel id="destaques" title="Destaques" icon={Star} products={sections?.destaques} accent="violet" />
        <SectionCarousel id="todos" title="Todos os produtos" icon={Package} products={sections?.todos} accent="blue" />

        <div className="pt-2 pb-6">
          <Link
            to={`/loja/${store.id}`}
            className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 border-blue-600 text-blue-700 font-semibold hover:bg-blue-50"
            data-testid="inst-see-all-products-bottom"
          >
            Ver todos os produtos desta loja <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      </div>

      {/* Etapa 3 — Avaliações da Loja */}
      <ReviewsSection targetType="store" targetId={store.id} />

      <BottomNav />
    </div>
  );
};

export default StoreInstitutional;
